import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "../AuthContext";
import {
	AI_CODE_REVIEWER_USERNAME,
	GITLAB_BASE_URL,
	GITLAB_GRAPHQL_URL,
	ITEMS_PER_PAGE,
} from "../constants";
import {
	ENRICHMENT_BATCH_SIZE,
	ENRICHMENT_CONCURRENCY,
	createEnrichmentQuery,
} from "./createEnrichmentQuery";
import { mergePipelines } from "./mergePipelines";
import { projectRules } from "./projectRules";
import { useCachedStorage } from "./useCachedStorage";

const REQUEST_TIMEOUT_MS = 30000;
const REPO_CACHE_TTL_HOURS = 1;
const REPO_CACHE_TTL_MS = REPO_CACHE_TTL_HOURS * 60 * 60 * 1000;
const REPO_MR_FETCH_SIZE = ITEMS_PER_PAGE * 3;
const PROJECT_FIELD_CONCURRENCY = 4;

const MR_FIELDS_LIGHTWEIGHT = `
  id
  iid
  webPath
  title
  createdAt
  updatedAt
  state
  draft
  reference
  sourceBranch
  conflicts
  userNotesCount
  author {
    avatarUrl
    name
    username
    bot
  }
  project {
    archived
    fullPath
  }
`;

const PROJECT_SEARCH_QUERY = `
  query searchProjects($search: String!) {
    projects(search: $search, membership: true, first: 20) {
      nodes {
        id
        name
        nameWithNamespace
        fullPath
        webUrl
        archived
      }
    }
  }
`;

const PROJECT_MERGE_REQUESTS_QUERY = `
  query getProjectMergeRequests(
    $fullPath: ID!
    $first: Int!
    $after: String
  ) {
    project(fullPath: $fullPath) {
      id
      name
      nameWithNamespace
      fullPath
      webUrl
      mergeRequests(first: $first, after: $after, sort: UPDATED_DESC) {
        count
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          ${MR_FIELDS_LIGHTWEIGHT}
        }
      }
    }
  }
`;

export const isRepositoryCacheFresh = (data) => {
	if (!data?.loadedAt) return false;
	return Date.now() - new Date(data.loadedAt).getTime() < REPO_CACHE_TTL_MS;
};

const runWithConcurrency = async (tasks, concurrency) => {
	const results = new Array(tasks.length);
	let nextTask = 0;

	const workers = Array.from(
		{ length: Math.min(concurrency, tasks.length) },
		async () => {
			while (nextTask < tasks.length) {
				const taskIndex = nextTask++;
				results[taskIndex] = await tasks[taskIndex]();
			}
		},
	);

	await Promise.all(workers);
	return results;
};

const isAiCodeReviewer = (user) => user?.username === AI_CODE_REVIEWER_USERNAME;

const dedupeMRs = (mrs) => {
	const seen = new Set();
	const result = [];
	for (const mr of mrs) {
		if (!mr?.id || seen.has(mr.id)) continue;
		seen.add(mr.id);
		result.push(mr);
	}
	return result;
};

export const useRepositoryMergeRequests = () => {
	const { user } = useAuth();
	const [repoData, setRepoData] = useCachedStorage(
		"repos-page-merge-request-cache",
		{},
		REPO_CACHE_TTL_HOURS,
	);
	const repoDataRef = useRef(repoData);

	useEffect(() => {
		repoDataRef.current = repoData;
	}, [repoData]);

	useEffect(() => {
		setRepoData((prev) => {
			let changed = false;
			const next = {};

			for (const [fullPath, data] of Object.entries(prev)) {
				next[fullPath] = data?.loading ? { ...data, loading: false } : data;
				if (data?.loading) changed = true;
			}

			return changed ? next : prev;
		});
	}, [setRepoData]);

	const executeQuery = useCallback(
		async (query, variables = {}) => {
			const controller = new AbortController();
			const timeoutId = setTimeout(
				() => controller.abort(),
				REQUEST_TIMEOUT_MS,
			);

			try {
				const response = await fetch(GITLAB_GRAPHQL_URL, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${user?.access_token}`,
					},
					body: JSON.stringify({ query, variables }),
					signal: controller.signal,
				});

				if (!response.ok) {
					throw new Error("GraphQL request failed");
				}

				const result = await response.json();
				if (result.errors) {
					throw new Error(
						result.errors[0]?.message || "GraphQL request failed",
					);
				}

				return result.data;
			} catch (error) {
				if (error.name === "AbortError") {
					throw new Error("GitLab request timed out");
				}
				throw error;
			} finally {
				clearTimeout(timeoutId);
			}
		},
		[user?.access_token],
	);

	const avatarUrl = useCallback((url) => {
		if (!url) return null;
		return url.startsWith("https://") ? url : `${GITLAB_BASE_URL}${url}`;
	}, []);

	const normalizeProject = useCallback(
		(project, fallback = {}) => ({
			id: project?.id || fallback.id,
			name: project?.name || fallback.name,
			nameWithNamespace:
				project?.nameWithNamespace || fallback.nameWithNamespace,
			fullPath: project?.fullPath || fallback.fullPath,
			webUrl: project?.webUrl || fallback.webUrl,
		}),
		[],
	);

	const transformMergeRequest = useCallback(
		(mr) => {
			if (!mr) return null;

			const pipeline = mergePipelines(mr);
			const approvers = mr.approvedBy?.nodes || [];
			const reviewers = mr.reviewers?.nodes || [];
			const aiReviewMissing = !approvers.some(isAiCodeReviewer);
			const hasHumanReview =
				approvers.some((reviewer) => !isAiCodeReviewer(reviewer)) ||
				reviewers.some((reviewer) => {
					const reviewState = reviewer.mergeRequestInteraction?.reviewState;
					return (
						!isAiCodeReviewer(reviewer) &&
						reviewState &&
						!["UNREVIEWED", "REVIEW_STARTED"].includes(reviewState)
					);
				});

			return {
				id: mr.id,
				iid: mr.iid,
				webUrl: `${GITLAB_BASE_URL}${mr.webPath}`,
				title: mr.title,
				createdAt: mr.createdAt,
				updatedAt: mr.updatedAt,
				state: mr.state,
				draft: mr.draft,
				reference: mr.reference,
				sourceBranch: mr.sourceBranch,
				projectPath:
					mr.project?.fullPath ||
					mr.reference?.split("!")[0] ||
					mr.webPath?.split("/-/")[0]?.split("/").slice(-2).join("/") ||
					"",
				userNotesCount: mr.userNotesCount,
				conflicts: mr.conflicts,
				author: {
					name: mr.author?.name || "Unknown",
					username: mr.author?.username || "",
					avatarUrl: avatarUrl(mr.author?.avatarUrl),
					bot: mr.author?.bot || mr.author?.username === "renovate",
					isMe: user?.profile?.username === mr.author?.username,
				},
				diffStatsSummary: mr.diffStatsSummary || {
					additions: 0,
					changes: 0,
					deletions: 0,
				},
				discussions: Math.max(
					0,
					(mr.resolvableDiscussionsCount || 0) -
						(mr.resolvedDiscussionsCount || 0),
				),
				aiReviewMissing,
				hasHumanReview,
				headPipeline:
					(pipeline && {
						status: pipeline.active ? "running" : pipeline.status.toLowerCase(),
						latest: pipeline.latest,
						name: pipeline.name,
						totalJobs: pipeline.totalJobs?.count || 0,
						failedJobs: pipeline.failedJobs?.count || 0,
						passedJobs: pipeline.passedJobs?.count || 0,
						mergeRequestEventType: pipeline.mergeRequestEventType,
					}) ||
					null,
				approvalsLeft: mr.approvalsLeft,
				approvers: approvers.map((reviewer) => ({
					avatarUrl: avatarUrl(reviewer.avatarUrl),
					name: reviewer.name,
					username: reviewer.username,
				})),
				reviewers,
				myReview:
					(mr.reviewers?.nodes || []).filter(
						(r) => r.username === user?.profile?.username,
					)?.[0]?.mergeRequestInteraction?.reviewState ||
					((mr.approvedBy?.nodes || []).find(
						(reviewer) => reviewer.username === user?.profile?.username,
					)
						? "APPROVED"
						: "UNREVIEWED"),
				project: mr.project,
				...(mr.projectFields && { projectFields: mr.projectFields }),
			};
		},
		[avatarUrl, user?.profile?.username],
	);

	const enrichMergeRequestsWithExpensiveFields = useCallback(
		async (mrs) => {
			if (!mrs || mrs.length === 0) return;

			const tasks = [];
			for (let i = 0; i < mrs.length; i += ENRICHMENT_BATCH_SIZE) {
				const batch = mrs.slice(i, i + ENRICHMENT_BATCH_SIZE);
				tasks.push(async () => {
					const data = await executeQuery(createEnrichmentQuery(batch));
					for (const [index, originalMR] of batch.entries()) {
						const enrichedMR = data[`mergeRequest${index}`];
						if (!enrichedMR) continue;
						Object.assign(originalMR, {
							diffStatsSummary: enrichedMR.diffStatsSummary,
							pipelines: enrichedMR.pipelines,
							headPipeline: enrichedMR.headPipeline,
							approvedBy: enrichedMR.approvedBy,
							reviewers: enrichedMR.reviewers,
							approvalsLeft: enrichedMR.approvalsLeft,
							resolvableDiscussionsCount: enrichedMR.resolvableDiscussionsCount,
							resolvedDiscussionsCount: enrichedMR.resolvedDiscussionsCount,
						});
					}
				});
			}

			await runWithConcurrency(tasks, ENRICHMENT_CONCURRENCY);
		},
		[executeQuery],
	);

	const fetchProjectMergeRequestFields = useCallback(
		async (mrs) => {
			const projectMrs = {};

			for (const mr of mrs) {
				const projectPath = mr.project?.fullPath;
				if (!projectPath || !projectRules?.[projectPath]?.mergeRequestFields) {
					continue;
				}
				projectMrs[projectPath] ??= {};
				projectMrs[projectPath][mr.iid] = mr;
			}

			const tasks = [];
			for (const projectFullPath in projectMrs) {
				const projectRule = projectRules[projectFullPath];
				const allIids = Object.keys(projectMrs[projectFullPath]);
				const fieldBatches = [
					{ fields: projectRule.mergeRequestFields, iids: allIids },
				];

				if (projectRule.batchedMergeRequestFields) {
					const { batchSize, fields } = projectRule.batchedMergeRequestFields;
					for (let i = 0; i < allIids.length; i += batchSize) {
						fieldBatches.push({
							fields,
							iids: allIids.slice(i, i + batchSize),
						});
					}
				}

				for (const { fields, iids } of fieldBatches) {
					tasks.push(async () => {
						const iidList = iids.map((iid) => JSON.stringify(iid)).join(",");
						const query = `
              query getRepoPageProjectMrFields {
                project(fullPath: ${JSON.stringify(projectFullPath)}) {
                  mergeRequests(iids: [${iidList}]) {
                    nodes {
                      iid
                      ${fields}
                    }
                  }
                }
              }
            `;

						const data = await executeQuery(query);
						const nodes = data.project?.mergeRequests?.nodes || [];
						for (const node of nodes) {
							const mr = projectMrs[projectFullPath][node.iid];
							if (mr) mr.projectFields = { ...mr.projectFields, ...node };
						}
					});
				}
			}

			await runWithConcurrency(tasks, PROJECT_FIELD_CONCURRENCY);
		},
		[executeQuery],
	);

	const applyProjectRules = useCallback((mrs) => {
		for (const mr of mrs) {
			const projectPath = mr.project?.fullPath;
			mr.additionalRules = projectRules?.[projectPath]?.additionalRules?.(mr);
		}
	}, []);

	const searchProjects = useCallback(
		async (search) => {
			const term = search.trim();
			if (!user?.access_token || term.length < 2) return [];

			const data = await executeQuery(PROJECT_SEARCH_QUERY, { search: term });
			return (data.projects?.nodes || [])
				.filter((project) => project && !project.archived)
				.map((project) => normalizeProject(project));
		},
		[executeQuery, normalizeProject, user?.access_token],
	);

	const loadRepoMergeRequests = useCallback(
		async (repo, { reset = false, force = false } = {}) => {
			if (!user?.access_token || !repo?.fullPath) return;

			const existing = repoDataRef.current[repo.fullPath];
			if (existing?.loading && !force) return;

			const shouldReset = reset || force || !isRepositoryCacheFresh(existing);

			if (!shouldReset && existing?.pageInfo?.hasNextPage === false) return;

			const after = shouldReset ? null : existing?.pageInfo?.endCursor || null;

			setRepoData((prev) => ({
				...prev,
				[repo.fullPath]: {
					...prev[repo.fullPath],
					project: repo,
					mrs: prev[repo.fullPath]?.mrs || [],
					error: "",
					loading: true,
				},
			}));

			try {
				const data = await executeQuery(PROJECT_MERGE_REQUESTS_QUERY, {
					fullPath: repo.fullPath,
					first: REPO_MR_FETCH_SIZE,
					after,
				});
				const project = data.project;
				const connection = project?.mergeRequests;
				const nodes = (connection?.nodes || []).filter((mr) => mr != null);

				await Promise.all([
					enrichMergeRequestsWithExpensiveFields(nodes),
					fetchProjectMergeRequestFields(nodes),
				]);

				const transformedMRs = nodes
					.map(transformMergeRequest)
					.filter((mr) => mr && !mr.project?.archived);
				applyProjectRules(transformedMRs);

				setRepoData((prev) => {
					const previousMRs = shouldReset ? [] : prev[repo.fullPath]?.mrs || [];
					const mrs = dedupeMRs([...previousMRs, ...transformedMRs]);

					return {
						...prev,
						[repo.fullPath]: {
							project: {
								...normalizeProject(project, repo),
							},
							mrs,
							totalCount: connection?.count ?? mrs.length,
							pageInfo: connection?.pageInfo || {
								endCursor: null,
								hasNextPage: false,
							},
							loading: false,
							error: "",
							loadedAt: new Date().toISOString(),
						},
					};
				});
			} catch (error) {
				setRepoData((prev) => ({
					...prev,
					[repo.fullPath]: {
						...prev[repo.fullPath],
						project: repo,
						loading: false,
						error: prev[repo.fullPath]?.mrs?.length
							? `Showing cached data. Last refresh failed: ${error.message}`
							: error.message,
					},
				}));
			}
		},
		[
			applyProjectRules,
			enrichMergeRequestsWithExpensiveFields,
			executeQuery,
			fetchProjectMergeRequestFields,
			normalizeProject,
			setRepoData,
			transformMergeRequest,
			user?.access_token,
		],
	);

	const clearRepoData = useCallback(
		(fullPath) => {
			setRepoData((prev) => {
				const next = { ...prev };
				delete next[fullPath];
				return next;
			});
		},
		[setRepoData],
	);

	return {
		repoData,
		searchProjects,
		loadRepoMergeRequests,
		clearRepoData,
	};
};
